import { useQuery } from "@hooks/useQuery";
import { Icon } from "@iconify/react";
import { Button, DatePicker, message, Pagination, TableColumnsType } from "antd";
import { ColumnType } from "antd/lib/table";
import { AxiosInstance } from "axios";
import { debounce } from "lodash";
import moment from "moment";
import Papa from "papaparse";
import { useCallback, useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper, DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import ExcelModal from "~/Components/Modal/ExcelModal";
import { useNotification } from "~/Hooks/useNotification";
import { eramanBEApi } from "~/Services";
import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";
import { renderDateColumn, renderDDMMMYYYYFormat } from "~/Utils/TableRenderFunction";
import { useEpcApplicationDocumentDetails } from "./EpcApplication.hook";
const { RangePicker } = DatePicker;

export interface ExportParams {
  sortOrder: string;
  orderByName: string;
  searchString: string;
  selectedStatusType: string;
  selectedCardType: string;
  startDateType: string;
  endDateType: string;
}

// Helper function to fetch all data using totalRows
export const fetchAllData = async (apiUrl: string, totalRows: number, apiClient: AxiosInstance): Promise<any[]> => {
  const url = `${apiUrl}&pageNo=1&pageSize=${totalRows}`;
  const response = await apiClient.get(url);
  if (!response.data?.data) {
    throw new Error("No data received from API");
  }
  return response.data.data;
};

// Helper function to determine if a column is a ColumnType (not a group)
function isColumnType<T>(column: ColumnType<T> | any): column is ColumnType<T> {
  return "dataIndex" in column;
}

// Updated createExportHandler
export const createExportHandler = async (
  apiClient: AxiosInstance,
  apiUrl: string,
  columns: TableColumnsType<any>,
  exportFileName: string,
  selectedKeys: React.Key[] = [],
  totalRows?: number
): Promise<void> => {
  try {
    let exportData;
    if (totalRows && selectedKeys.length === 0) {
      // Fetch all data if totalRows is provided and no specific rows are selected
      exportData = await fetchAllData(apiUrl, totalRows, apiClient);
    } else {
      const response = await apiClient.get(apiUrl);
      if (!response.data?.data) {
        throw new Error("No data received from API");
      }
      exportData = response.data.data;
    }

    console.log("exportDataaaaaaa", exportData);

    // Filter by selected keys if provided (using 'id' instead of index)
    if (selectedKeys.length > 0) {
      exportData = exportData.filter((row: any) => selectedKeys.includes(row.id)); // Use 'id' to match selected keys
    }

    const exportColumns = columns.filter(
      (col): col is ColumnType<any> =>
        isColumnType(col) && typeof col.dataIndex === "string" && col.dataIndex !== "actions"
    );

    const dateFields = [
      "createdAt",
      "updatedAt",
      "deletedAt",
      "expiryAt",
      "epcJoinedAt",
      "epcUpdatedAt",
      "epcViewedAt",
    ];

    const formattedData = exportData.map((record: any) => {
      const row: { [key: string]: any } = {};

      exportColumns.forEach((col) => {
        const dataIndex = col.dataIndex as string;
        const title = col.title as string;

        let value = record[dataIndex];

        if (col.render && typeof col.render === "function") {
          try {
            const renderedValue = col.render(value, record, 0);
            if (typeof renderedValue !== "object") {
              value = renderedValue;
            }
          } catch (e) {
            // Keep original value if render fails
          }
        }

        if (dateFields.includes(dataIndex) && value) {
          value = moment(value).format("YYYY-MM-DD HH:mm:ss");
        }

        row[title] = value ?? "";
      });

      return row;
    });
    console.log("formatted Dataa", formattedData);
    if (formattedData.length > 0) {
      const csv = Papa.unparse(formattedData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${exportFileName}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      console.log("No data to export for selected keys");
      throw new Error("No data selected for export");
    }
  } catch (error) {
    console.error("Error exporting data:", error);
    throw error;
  }
};

export const EpcApplications = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [searchString, setSearchString] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [data, setData] = useState<any>([]);
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [orderByName, setOrderByName] = useState<string>("epcJoinedAt");
  const [id, setId] = useState<string | undefined>(selectedRowData?.id);
  const [selectedStatusType, setSelectedStatusType] = useState<any>("");
  const [selectedCardType, setSelectedCardType] = useState<any>("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const { openToast } = useNotification();
  const [file, setFile] = useState<any>(null);
  const [importModalVisible, setImportModalVisible] = useState(file ? true : false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [startDateType, setStartDateType] = useState<any>("");
  const [endDateType, setEndDateType] = useState<any>("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const statusType: TypeData[] = [
    {
      label: "All",
      value: "All",
    },
    {
      label: "Active",
      value: "Active",
    },
    // {
    //   label: "Pending Approval (New EPC)",
    //   value: "Pending Approval (New EPC)",
    // },
    {
      label: "Pending Approval (New Upgrade)",
      value: "Pending Approval (New Upgrade)",
    },
    {
      label: "Pending Approval (New Renewal)",
      value: "Pending Approval (New Renewal)",
    },
    {
      label: "Expired",
      value: "Expired",
    },
    {
      label: "Suspended",
      value: "Suspended",
    },
  ];
  const cardType: TypeData[] = [
    {
      label: "CLASSIC",
      value: "CLASSIC",
    },
    {
      label: "PREFERRED",
      value: "PREFERRED",
    },
    {
      label: "PRIORITY",
      value: "PRIORITY",
    },
    // {
    //   label: "Elite",
    //   value: "Elite",
    // },
  ];

  const {
    data: drilldown,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchDocument,
  } = useEpcApplicationDocumentDetails(id || "");

  useEffect(() => {
    setImportModalVisible(true);
  }, [file]);

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  const getRecords = useCallback(
    (
      sortOrderParam: string,
      orderByParam: string,
      searchStringParam: string,
      currentPageParam: number,
      currentPageSizeParam: number,
      startDateType: string,
      endDateType: string
    ) => {
      setIsLoading(true);
      let url = `/admin/epc?pageNo=${currentPageParam}&pageSize=${currentPageSizeParam || 10}&sortBy=${orderByParam ? orderByParam : ""}&sortOrder=${sortOrderParam ? sortOrderParam : ""}&status=${selectedStatusType || ""}&cardType=${selectedCardType || ""}&search=${searchStringParam || ""}&firstName=${searchStringParam || ""}`; // &fromDate=${startDateType? startDateType:""}&toDate=${endDateType? endDateType:""}

      eramanBEApi
        // getEramanBE(url)
        .get(url)
        .then((res: any) => {
          if (res && res.data) {
            setTotalRecords(res.data.totalRows);
            setData(res.data.data);
          }
        })
        .catch((err) => {
          setData([]);
          console.error("Error fetching data:", err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [selectedStatusType, selectedCardType]
  );

  useEffect(() => {
    getRecords(sortOrder, orderByName, searchString, currentPage, pageSize, startDateType, endDateType);
  }, [getRecords, sortOrder, orderByName, searchString, currentPage, pageSize, startDateType, endDateType]);

  const columns: TableColumnsType = [
    {
      title: "Card Type",
      dataIndex: "epcType",
      key: "epcType",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "epcType"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "epcType"
            ? "descend"
            : undefined,
    },
    {
      title: "EPC Id",
      dataIndex: "epcId",
      key: "epcId",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "epcId"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "epcId"
            ? "descend"
            : undefined,
    },
    {
      title: "Status",
      dataIndex: "epcStatus",
      key: "epcStatus",
    },
    {
      title: "Name",
      dataIndex: "firstName",
      key: "firstName",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "firstName"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "firstName"
            ? "descend"
            : undefined,
    },
    {
      title: "NRIC",
      dataIndex: "nric",
      key: "nric",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "nric"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "nric"
            ? "descend"
            : undefined,
    },
    {
      title: "Email",
      dataIndex: "emailAddress",
      key: "emailAddress",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "emailAddress"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "emailAddress"
            ? "descend"
            : undefined,
    },
    {
      title: "Joined At",
      dataIndex: "epcJoinedAt",
      key: "epcJoinedAt",
      render: (value: any) => renderDateColumn(value),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "epcJoinedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "epcJoinedAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "epcUpdatedAt",
      key: "epcUpdatedAt",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "epcUpdatedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "epcUpdatedAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Viewed At",
      dataIndex: "epcViewedAt",
      key: "epcViewedAt",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "epcViewedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "epcViewedAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Expiry Date",
      dataIndex: "epcExpiry",
      key: "epcExpiry",
      render: renderDDMMMYYYYFormat,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "epcExpiry"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "epcExpiry"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_: any, record: any) => {
        console.log("record", record);
        return (
          <div className="flex gap-2">
            <div className="relative inline-block">
              <Icon
                height={25}
                icon={"carbon:edit"}
                className="text-orange-500 border border-orange-500 rounded-full p-0.5"
                onClick={(e: any) => {
                  e.preventDefault();
                  navigate(`/dedicated/eraman/epc-applications/add?editId=${record.id}`);
                }}
              />
              {!record?.epcViewedAt && (
                <span className="absolute top-0 right-0 block w-2.5 h-2.5 bg-blue-500 rounded-full" />
              )}
            </div>
            <Icon
              height={25}
              icon={"material-symbols:delete-outline"}
              style={{
                color: "var(--Primary-Color, #DA322D)",
                border: "1.25px solid var(--Primary-Color, #DA322D)",
                borderRadius: "50%",
                padding: "2px",
                boxSizing: "border-box",
              }}
              onClick={(e) => {
                e.preventDefault();
                setDeleteModalVisible(true);
              }}
            />
            {/* <Icon
              height={25}
              icon={"line-md:document-twotone"}
              className="text-orange-500 border border-orange-500 rounded-full p-0.5"
              onClick={(e) => {
                e.preventDefault();
                navigate(`/dedicated/eraman/epc-applications/user-transactions`);
              }}
            /> */}
          </div>
        );
      },
    },
  ];

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";
    if (column.sorter) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
        order = "desc";
      } else if (sortOrder === "desc") {
        setSortOrder("asc");
        order = "asc";
      }
    }
    setOrderByName(column.dataIndex);
    setCurrentPage(1);
    // getRecords(order, column.dataIndex, searchString, currentPage);
  };

  const handleSearchChange = debounce((value: string, isLoading: boolean) => {
    setSearchString(value);
    setIsLoading(isLoading);
    setCurrentPage(1);
  }, 1000);

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING"
  ) => {
    if (apiUrl === "") return;
    const payload = data;
    try {
      let response = await eramanBEApi.delete(apiUrl, payload);
      console.log("Response:", response);
      openToast({
        content: "Data Deleted Successfully",
        type: "success",
      });
      getRecords(sortOrder, orderByName, searchString, currentPage, pageSize, startDateType, endDateType);
    } catch (error: any) {
      console.error("Error:", error);

      if (error.response) {
        const errorMessage = error.response.data || "An unexpected error occurred.";
        message.error(errorMessage);
      } else {
        message.error("Network error or server not reachable.");
      }
    }
  };

  async function sendImportEpcRequest() {
    try {
      const formData = new FormData();
      formData.append("file", file.fileList[0].originFileObj);
      let url = `/admin/epc/file`;
      eramanBEApi
        .post(url, formData)
        .then((response) => {
          openToast({
            content: "Data Sent for Review",
            type: "success",
          });
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to send data",
            type: "error",
          });
        });
    } catch (err) {}
  }

  async function sendDeleteUserRequest() {
    try {
      await onSubmit(selectedRowData, `/admin/epc?id=${selectedRowData?.id}`, "DELETE", "DELETE", "PENDING");
    } catch (err) {}
  }

  // Updated handleExportData
  async function handleExportData(isExportAll: boolean) {
    try {
      const baseApiUrl = `/admin/epc?sortBy=${orderByName || ""}&sortOrder=${sortOrder || ""}&status=${selectedStatusType || ""}&cardType=${selectedCardType || ""}&search=${searchString || ""}&fromDate=${startDateType || ""}&toDate=${endDateType || ""}`;

      const apiUrl = isExportAll ? baseApiUrl : `${baseApiUrl}&pageNo=${currentPage}&pageSize=${pageSize || 10}`;

      await createExportHandler(
        eramanBEApi,
        apiUrl,
        columns,
        "EPC_APPLICATION",
        isExportAll ? [] : selectedRowKeys,
        isExportAll ? totalRecords : undefined
      );
    } catch (error) {
      openToast({
        content: "Failed to export data",
        type: "error",
      });
    }
  }

  return (
    <ContentWrapper
      title={"EPC Applications"}
      actionItems={
        <Button
          onClick={() => {
            navigate("/dedicated/eraman/epc-applications/add");
          }}
          icon={<Icon icon={"lucide:plus"} />}
        >
          Add New EPC
        </Button>
      }
    >
      <DataTable
        tabOffset={60}
        columns={columns.map((col: any) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        className="table-clickable"
        tableData={
          data
            ? data?.map((d: any, index: number) => ({
                ...d,
                key: d.id,
              }))
            : []
        }
        isLoading={isLoading}
        exportFileName="EPC_APPLICATION"
        exportHandler={handleExportData}
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={setSelectedRowKeys}
        isPagination={false}
        recordsPerPage={10}
        multiSelect={true}
        hideExport={false}
        totalRecords={totalRecords}
        exportApiUrl=""
        hideImport={true}
        onFileUpload={setFile}
        isServerSearchApi={true}
        onSearchChange={handleSearchChange}
        onClick={(record) => {
          setSelectedRowData(record);
        }}
        searchPlaceholderText={"Search by EPC Id/ Name/ Email"}
        additionalCTA={
          <div className="flex items-center space-x-2">
            {/* <RangePicker
                  value={[
                    startDateType ? moment(startDateType) : null,
                    endDateType ? moment(endDateType) : null
                  ]}
                  onChange={(dates, dateStrings) => {
                    const [start, end] = dateStrings;  
                    setStartDateType(start ? moment(start).format('YYYY-MM-DD') : ''); 
                    setEndDateType(end ? moment(end).format('YYYY-MM-DD') : '');      
                  }}
                  format="YYYY-MM-DD"
                  placeholder={["Select Joined Start Date", "Select Joined End Date"]}
                  className="mr-2 w-80"
                  onKeyDown={(e)=>e.preventDefault()}
                /> */}
            <ActionDropdown
              value={selectedCardType}
              options={cardType}
              onChange={setSelectedCardType}
              placeholder="Select Card Type"
              className="w-64"
            />
            <ActionDropdown
              value={selectedStatusType}
              options={statusType}
              onChange={setSelectedStatusType}
              placeholder="Select Status"
              className="w-64"
            />
          </div>
        }
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage}
          total={totalRecords}
          pageSize={pageSize}
          // onChange={(curPage) => {
          //   let page = curPage;
          //   setCurrentPage(page);
          //   // getRecords(sortOrder, orderByName, searchString, page);
          // }}
          onChange={(curPage, newPageSize) => {
            console.log("newPageSize", newPageSize, curPage);
            setCurrentPage(curPage);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(1);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
        />
      </div>
      <ConfirmationModal
        type="deleteWithNoReview"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteUserRequest}
      />
      {file && (
        <ExcelModal
          onSubmit={sendImportEpcRequest}
          file={file}
          open={importModalVisible}
          onClose={() => {
            setImportModalVisible(false);
            setFile(null);
          }}
        />
      )}
    </ContentWrapper>
  );
};
